import React from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import {Col, Skeleton, Spin, Tabs} from 'antd';
import {CalendarOutlined} from '@ant-design/icons';
import dayjs from 'dayjs';

import {messageType, showMessages} from '../../../helpers/message';
import {isEstimatedCost, isWithinGracePeriod} from '../../../helpers/booking';
import PageTitle from '../../../components/page/PageTitle';
import DetailsScreenHeader from '../../../components/detailsScreenHeader/DetailsScreenHeader';
import BookingAddIncrement from '../../../components/booking/BookingAddIncrement';
import BookingDrivingLog from '../../../components/booking/BookingDrivingLog';
import BookingCancel from '../../../components/booking/BookingCancel';
import BookingEnd from '../../../components/booking/BookingEnd';
import ErrorBoundary from '../../../components/ErrorBoundary';
import BookingDetails from '../../../components/booking/BookingDetails';
import Comments from '../Comments';
import BookingNoteCard from '../../../components/booking/BookingNoteCard';
import BookingHistoryCard from '../../../components/booking/BookingHistoryCard';
import BookingDiscount from '../../../components/booking/BookingDiscount';
import {useGetBookingQuery} from '../../../store/api/bookingsApi';
import {useGetBookingMembershipsQuery} from '../../../store/api/accountApi';
import {useAppSelector} from '../../../hooks/useStore';
import {selectHasStaffRole} from '../../../store/slices/userSlice';
import {skipToken} from '@reduxjs/toolkit/query/react';
import {BaseError} from '../../../types/apiTypes';
import {Car} from '../../../types/carTypes';

import './BookingManage.less';

const BookingManage = () => {
    const navigate = useNavigate();
    const params = useParams();
    const bookingID = Number(params.bookingID);
    const isMCT = useAppSelector(state => selectHasStaffRole(state, ['member-care', 'developer']));
    const isRegStaff = useAppSelector(state => selectHasStaffRole(state, ['regular']));

    const [showBookingCancel, setShowBookingCancel] = React.useState(false);
    const [showEndTrip, setShowEndTrip] = React.useState(false);
    const [showBookingAddIncrement, setShowBookingAddIncrement] = React.useState(false);
    const [isCostEstimated, setIsCostEstimated] = React.useState(true);
    const [isCancelledBooking, setIsCancelledBooking] = React.useState(false);

    const {data: booking, isFetching: bookingLoading, isError: isBookingError, error: bookingError} = useGetBookingQuery(bookingID);
    const {data: eligibleDrivers, isFetching: eligibleDriversLoading, isError: isEligibleDriversError, error: eligibleDriversError} = useGetBookingMembershipsQuery(isMCT ? bookingID : skipToken);

    React.useEffect(() => {
        if (isBookingError) {
            showMessages((bookingError as BaseError).data?.message, messageType.error);
            if (!booking) navigate('/404');
        }
        if (isEligibleDriversError) {
            showMessages((eligibleDriversError as BaseError).data?.message, messageType.error);
        }
    }, [booking, bookingError, eligibleDriversError, isBookingError, isEligibleDriversError, navigate]);

    const onClickBack = () => {
        let tab;
        const ended_at = booking?.booking_deployment.ended_at;
        const started_at = booking?.booking_deployment.started_at;
        const now = dayjs().tz();

        const isBeforeStart = now.isBefore(started_at);
        const isBetweenStartAndEnd = now.isAfter(started_at) && now.isBefore(ended_at);
        const isAfterEnd = now.isAfter(ended_at);

        if (isCancelledBooking) {
            tab = 'cancelled';
        } else if (isBeforeStart) {
            tab = 'upcoming';
        } else if (isBetweenStartAndEnd) {
            tab = 'current';
        } else if (isAfterEnd) {
            tab = 'past';
        }
        navigate('/booking/list', {state: {tab}});
    };

    React.useEffect(() => {
        // Handle the booking functionality state changes.
        if (booking) {
            const deploymentDetails = booking.booking_deployment;

            // MCT doesn't have superior access to these functionalities, so combine into one var for these checks.
            const isStaff = isMCT || isRegStaff;
            const now = dayjs().tz();
            const startedAt = dayjs(deploymentDetails.started_at).tz();
            const endedAt = dayjs(deploymentDetails.ended_at).tz();
            const createdAt = dayjs(booking.booking_created_at).tz();
            const isCancelled = deploymentDetails.state === 'CANCELLED';
            const isInGracePeriod = isWithinGracePeriod(createdAt, now);
            const isBookingCurrent = now.isAfter(startedAt) && now.isBefore(endedAt);

            // Added in ticket #642, allow (only) MCT to end trip whenever the booking is ongoing.
            setShowEndTrip(isMCT && isBookingCurrent);

            if (isCancelled) {
                // If booking cancelled, don't show buttons.
                setShowBookingCancel(false);
                setShowEndTrip(false);
                setIsCancelledBooking(true);
            } else if (booking.invoiced || booking.reconciled) {
                // Hide cancel booking button if booking has been reconciled or invoiced.
                setShowBookingCancel(false);
            } else if (!isStaff && (isInGracePeriod || (now.isBefore(startedAt)))) {
                // Allow booking cancel if booking is within grace period or has not yet started.
                setShowBookingCancel(true);
            } else if (isStaff) {
                // Default to showing booking cancel button for staff.
                setShowBookingCancel(true);
            }

            if (!isCancelled && isBookingCurrent) {
                // Allow extending a booking if it's in progress.
                setShowBookingAddIncrement(true);
                // Only show endTrip when outside the grace period for members.
                if (!isStaff && !isInGracePeriod) setShowEndTrip(true);
            } else {
                setShowBookingAddIncrement(false);
            }

            // Change cost status to non-estimated if booking has been cancelled, has no distance, or the trip ended.
            if (!isEstimatedCost(deploymentDetails.state, booking.costs, now, endedAt)) {
                setIsCostEstimated(false);
            }
        }
    }, [booking, isMCT, isRegStaff]);

    // Show skeleton while data loads to avoid excessive type checking below.
    if (!booking) return <Skeleton active/>;

    const car: Partial<Car> = {
        id: booking.deployable.id,
        deployable_description: booking.deployable.description,
        location_description: booking.booking_location.description,
        image: booking.car_image,
    };

    const tabs = [
        {
            label: 'Booking Details',
            key: '1',
            children:
                <ErrorBoundary>
                    <BookingDetails
                        bookingID={bookingID}
                        bookingDetails={booking}
                        bookingDeploymentDetails={booking.booking_deployment}
                        eligibleDrivers={eligibleDrivers ?? []}
                        isEstimatedCost={isCostEstimated}
                        isCancelledBooking={isCancelledBooking}
                    />
                </ErrorBoundary>
        },
        {
            label: 'Driving Log',
            key: '2',
            children:
                <ErrorBoundary>
                    <BookingDrivingLog bookingDetails={booking}/>
                </ErrorBoundary>
        },
        {
            label: 'Comments',
            key: '3',
            children:
                <Comments
                    commentableType="App\Models\Bookings\Booking"
                    commentableID={bookingID}
                />
        },
        {
            label: 'Booking Note',
            key: '4',
            children:
                <BookingNoteCard
                    bookingID={bookingID}
                    initialMemberNote={booking.member_note}
                    isDisabled={isRegStaff || isMCT || booking.invoiced}
                />
        },
        {
            label: 'History',
            key: '5',
            children:
                <BookingHistoryCard
                    bookingID={bookingID}
                />
        }
    ];

    // Member: 'Booking Details', 'Driving Log', 'Booking Note', 'History'.
    let tabItems = [tabs[0], tabs[1], tabs[3], tabs[4]];

    if (isRegStaff) {
    // Staff: 'Booking Details', 'Driving Log' 'Comments', 'Booking Note', 'History'.
        tabItems = [tabs[0], tabs[1], tabs[2], tabs[3], tabs[4]];
    }

    return (
        <ErrorBoundary>
            <Spin spinning={bookingLoading || eligibleDriversLoading}>
                <PageTitle title="Booking Details" icon={<CalendarOutlined/>}/>
                <DetailsScreenHeader
                    car={car}
                    locationID={booking.booking_location.id}
                    bookingNumber={booking.booking_number}
                    onClickBack={onClickBack}
                >
                    <Col xs={24} md={8} className="booking-button-wrapper">
                        {
                            showBookingAddIncrement &&
                            <BookingAddIncrement
                                bookingID={bookingID}
                                bookingDetails={booking.booking_deployment}
                            />
                        }
                        {
                            isMCT &&
                            <BookingDiscount
                                bookingDiscounts={booking.discounts ?? []}
                                rateClass={booking.deployable.rate_class}
                                bookingID={bookingID}
                            />
                        }
                        {
                            showBookingCancel &&
                            <BookingCancel
                                bookingID={bookingID}
                                bookingStartTime={booking.booking_deployment.started_at}
                                bookingCreateTime={booking.booking_deployment.created_at}
                                hideBookingCancel={() => setShowBookingCancel(false)}
                                onCancelBooking={() => {
                                    setIsCostEstimated(false);
                                    setIsCancelledBooking(true);
                                }}
                            />
                        }
                        {
                            showEndTrip &&
                            <BookingEnd bookingID={bookingID} />
                        }
                    </Col>
                </DetailsScreenHeader>
                <Tabs defaultActiveKey="1" items={tabItems}/>
            </Spin>
        </ErrorBoundary>
    );
}

export default BookingManage;
